<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { VueFlow, useVueFlow, Panel } from '@vue-flow/core';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';

import FlowNode from './components/FlowNode.vue';
import StickyNote from './components/StickyNote.vue';
import NodeInspector from './components/NodeInspector.vue';
import ConditionPicker from './components/ConditionPicker.vue';
import ConditionConfig from './components/ConditionConfig.vue';
import { NODE_TYPES, PALETTE } from './constants';
import FlowsAPI from 'dashboard/api/flows';
import {
  blocksToNodes,
  blocksToEdges,
  createBlock,
  removeBlock,
  writeConnection,
  uid,
} from './graph';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { screenToFlowCoordinate, fitView } = useVueFlow();

const flow = ref(null);
const blocks = ref([]);
const selectedId = ref(null);
const isSaving = ref(false);
const isDirty = ref(false);
const menuOpen = ref(true);
const toolbarCollapsed = ref(false);

// drop-to-create popup
const dropPopup = ref(null);
let _pendingDrag = null; // raw JS var for DOM listener coordination

// condition picker
const conditionPickerBlockId = ref(null);

const openConditionPicker = blockId => {
  conditionPickerBlockId.value = blockId;
};

const onConditionSelected = condition => {
  const block = findBlock(conditionPickerBlockId.value);
  if (block) {
    if (!block.options.conditions) block.options.conditions = [];
    block.options.conditions.push(condition);
    markDirty();
  }
  conditionPickerBlockId.value = null;
};

// condition config (inline editor)
const conditionConfigTarget = ref(null);

const openConditionConfig = ({ blockId, index }) => {
  conditionConfigTarget.value = { blockId, index };
};

const activeCondition = computed(() => {
  if (!conditionConfigTarget.value) return null;
  const block = findBlock(conditionConfigTarget.value.blockId);
  if (!block) return null;
  return (block.options.conditions || [])[conditionConfigTarget.value.index] || null;
});

// edit modal
const editModalOpen = ref(false);
const editName = ref('');
const editDescription = ref('');

// undo/redo history
const history = ref([]);
const historyIndex = ref(-1);
const MAX_HISTORY = 40;

const pushHistory = () => {
  const snap = JSON.stringify(blocks.value);
  if (historyIndex.value >= 0 && history.value[historyIndex.value] === snap)
    return;
  history.value = history.value.slice(0, historyIndex.value + 1);
  history.value.push(snap);
  if (history.value.length > MAX_HISTORY) history.value.shift();
  historyIndex.value = history.value.length - 1;
};

const canUndo = computed(() => historyIndex.value > 0);
const canRedo = computed(
  () => historyIndex.value < history.value.length - 1
);

const undo = () => {
  if (!canUndo.value) return;
  historyIndex.value--;
  blocks.value = JSON.parse(history.value[historyIndex.value]);
  isDirty.value = true;
};

const redo = () => {
  if (!canRedo.value) return;
  historyIndex.value++;
  blocks.value = JSON.parse(history.value[historyIndex.value]);
  isDirty.value = true;
};

const flowId = computed(() => route.params.flowId);
const blockStats = ref({});

const nodes = computed(() => blocksToNodes(blocks.value, blockStats.value));
const edges = computed(() => blocksToEdges(blocks.value));

const flowDatasources = computed(() => {
  const ds = [];
  blocks.value.forEach(block => {
    if (block.type === 'api') {
      (block.options?.apis || []).forEach(api => {
        const name = api.options?.datasourceName;
        if (name) ds.push({ name, color: api.options?.datasourceColor || 'teal' });
      });
    }
  });
  return ds;
});

provide('flowDatasources', flowDatasources);

let statsInterval = null;

const fetchStats = async () => {
  try {
    const { data } = await FlowsAPI.stats(flowId.value);
    blockStats.value = data.block_stats || {};
  } catch (e) {
    // stats are optional
  }
};

const startStatsPolling = () => {
  stopStatsPolling();
  statsInterval = setInterval(fetchStats, 10000);
};

const stopStatsPolling = () => {
  if (statsInterval) {
    clearInterval(statsInterval);
    statsInterval = null;
  }
};

const selectedBlock = computed(() =>
  blocks.value.find(block => block.id === selectedId.value)
);

const markDirty = () => {
  isDirty.value = true;
  pushHistory();
};

const findBlock = id => blocks.value.find(block => block.id === id);

// ===== Drag-to-create: raw DOM listeners (bypass Vue Flow events) =====
const canvasWrapper = ref(null);

const handleMouseDown = e => {
  const handle = e.target.closest('.vue-flow__handle.source');
  if (!handle) return;
  const nodeEl = handle.closest('.vue-flow__node');
  _pendingDrag = {
    nodeId: nodeEl?.getAttribute('data-id') || handle.getAttribute('data-nodeid'),
    handleId: handle.getAttribute('data-handleid'),
  };
};

const handleMouseUp = e => {
  const captured = _pendingDrag;
  if (!captured) return;

  const cx = e.clientX;
  const cy = e.clientY;

  // Wait a tick so Vue Flow's onConnect fires first (clears _pendingDrag)
  setTimeout(() => {
    if (_pendingDrag !== captured) return; // onConnect cleared it
    _pendingDrag = null;

    const el = document.elementFromPoint(cx, cy);
    if (el && (el.closest('.vue-flow__handle') || el.closest('.vue-flow__node')))
      return;

    const flowPos = screenToFlowCoordinate({ x: cx, y: cy });
    dropPopup.value = {
      x: flowPos.x,
      y: flowPos.y,
      screenX: cx,
      screenY: cy,
      sourceId: captured.nodeId,
      sourceHandle: captured.handleId,
    };
  }, 80);
};

onMounted(async () => {
  // attach DOM listeners for drag-to-create
  document.addEventListener('mousedown', handleMouseDown, true);
  document.addEventListener('mouseup', handleMouseUp, true);

  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');

  try {
    const record = await store.dispatch('flows/show', flowId.value);
    flow.value = { ...record };
    blocks.value = JSON.parse(JSON.stringify(record.blocks || []));

    if (!blocks.value.some(block => block.type === 'trigger')) {
      blocks.value.push({
        id: 'trigger',
        type: 'trigger',
        presentation: { x: 0, y: 0 },
        options: NODE_TYPES.trigger.defaults(),
      });
    }
    pushHistory();
    fetchStats();
    startStatsPolling();
    setTimeout(() => fitView({ padding: 0.3 }), 60);
  } catch (error) {
    useAlert(t('FLOWS.API.LOAD_ERROR'));
    router.push({ name: 'flows_list' });
  }
});

onBeforeUnmount(() => {
  stopStatsPolling();
  document.removeEventListener('mousedown', handleMouseDown, true);
  document.removeEventListener('mouseup', handleMouseUp, true);
});

const addNodeAt = (type, position, sourceId, sourceHandle) => {
  const block = createBlock(type, position);
  blocks.value.push(block);

  if (sourceId && sourceHandle) {
    const source = findBlock(sourceId);
    if (source) writeConnection(source, sourceHandle, block.id);
  }

  if (type !== 'sticky') selectedId.value = block.id;
  markDirty();
};

const addNode = type => {
  const center = screenToFlowCoordinate({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  addNodeAt(
    type,
    {
      x: center.x + (Math.random() - 0.5) * 80,
      y: center.y + (Math.random() - 0.5) * 80,
    },
    null,
    null
  );
};

const removeNode = nodeId => {
  blocks.value = removeBlock(blocks.value, nodeId);
  if (selectedId.value === nodeId) selectedId.value = null;
  markDirty();
};

const duplicateNode = nodeId => {
  const original = findBlock(nodeId);
  if (!original) return;
  const clone = JSON.parse(JSON.stringify(original));
  clone.id = uid();
  clone.presentation = {
    x: (clone.presentation?.x || 0) + 40,
    y: (clone.presentation?.y || 0) + 40,
  };
  // clear connections on the clone
  if (clone.options) {
    for (const key of Object.keys(clone.options)) {
      if (key.endsWith('BlockId') || key.endsWith('NextBlockId')) {
        clone.options[key] = '';
      }
    }
  }
  blocks.value.push(clone);
  selectedId.value = clone.id;
  markDirty();
};

const onConnect = connection => {
  _pendingDrag = null; // clear so popup won't show
  const source = findBlock(connection.source);
  if (!source || connection.source === connection.target) return;

  writeConnection(source, connection.sourceHandle, connection.target);
  markDirty();
};

const onEdgeClick = ({ edge }) => {
  const source = findBlock(edge.source);
  if (!source) return;
  writeConnection(source, edge.sourceHandle, '');
  markDirty();
};

const onDropSelect = type => {
  if (!dropPopup.value) return;
  const { x, y, sourceId, sourceHandle } = dropPopup.value;
  addNodeAt(type, { x, y }, sourceId, sourceHandle);
  dropPopup.value = null;
};

const closeDropPopup = () => {
  dropPopup.value = null;
};

const onNodeDragStop = ({ node }) => {
  const block = findBlock(node.id);
  if (!block) return;
  block.presentation = {
    x: Math.round(node.position.x),
    y: Math.round(node.position.y),
  };
  isDirty.value = true;
  pushHistory();
};

const onNodeClick = ({ node }) => {
  const block = findBlock(node.id);
  if (block && block.type === 'sticky') {
    selectedId.value = null;
    return;
  }
  selectedId.value = node.id;
};

const save = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('flows/update', {
      id: flow.value.id,
      name: flow.value.name,
      description: flow.value.description,
      active: flow.value.active,
      blocks: blocks.value,
    });
    isDirty.value = false;
    useAlert(t('FLOWS.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('FLOWS.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const toggleActive = async () => {
  flow.value.active = !flow.value.active;
  await save();
};

const duplicateFlow = async () => {
  try {
    await store.dispatch('flows/clone', flow.value.id);
    useAlert(t('FLOWS.API.CLONE_SUCCESS'));
  } catch (error) {
    useAlert(t('FLOWS.API.CLONE_ERROR'));
  }
};

const exportFlow = () => {
  const data = {
    name: flow.value.name,
    description: flow.value.description,
    active: flow.value.active,
    blocks: blocks.value,
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${flow.value.name || 'flow'}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const openEditModal = () => {
  editName.value = flow.value.name || '';
  editDescription.value = flow.value.description || '';
  editModalOpen.value = true;
};

const saveEditModal = () => {
  flow.value.name = editName.value;
  flow.value.description = editDescription.value;
  isDirty.value = true;
  editModalOpen.value = false;
};

const deleteFlow = async () => {
  if (!window.confirm(t('FLOWS.CONFIRM_DELETE', { name: flow.value.name })))
    return;
  try {
    await store.dispatch('flows/delete', flow.value.id);
    useAlert(t('FLOWS.API.DELETE_SUCCESS'));
    router.push({ name: 'flows_list' });
  } catch (error) {
    useAlert(t('FLOWS.API.DELETE_ERROR'));
  }
};

const beforeUnload = event => {
  if (!isDirty.value) return;
  event.preventDefault();
  event.returnValue = '';
};
watch(isDirty, dirty => {
  if (dirty) window.addEventListener('beforeunload', beforeUnload);
  else window.removeEventListener('beforeunload', beforeUnload);
});

const PALETTE_COLORS = {
  chat: 'rgb(var(--blue-11))',
  action: 'rgb(var(--amber-11))',
  condition: 'rgb(var(--iris-11))',
  delay: 'rgb(var(--violet-11))',
  randomizer: 'rgb(var(--ruby-11))',
  api: 'rgb(var(--teal-11))',
  'field-operation': 'rgb(var(--slate-11))',
};
</script>

<template>
  <div ref="canvasWrapper" class="flex flex-col w-full h-full bg-n-background">
    <div class="flex flex-1 min-h-0">
      <!-- inspector side-panel (LEFT, DataCrazy style) -->
      <transition name="inspector">
        <NodeInspector
          v-if="selectedBlock && selectedBlock.type !== 'sticky'"
          :key="selectedBlock.id"
          :block="selectedBlock"
          @change="markDirty"
          @close="selectedId = null"
          @add-condition="openConditionPicker"
        />
      </transition>

      <div class="relative flex-1 min-w-0">
        <VueFlow
          :nodes="nodes"
          :edges="edges"
          :default-viewport="{ zoom: 0.8 }"
          :min-zoom="0.2"
          :max-zoom="1.6"
          :delete-key-code="null"
          :connection-line-style="{
            stroke: 'hsl(var(--gray, 0 0% 70%))',
            strokeWidth: 2,
            strokeDasharray: '5 5',
          }"
          class="dc-canvas w-full h-full"
          @connect="onConnect"
          @edge-click="onEdgeClick"
          @node-drag-stop="onNodeDragStop"
          @node-click="onNodeClick"
          @pane-click="selectedId = null; closeDropPopup()"
        >
          <template #node-flowBlock="nodeProps">
            <FlowNode
              v-bind="nodeProps"
              @remove="removeNode"
              @duplicate="duplicateNode"
              @change="markDirty"
              @add-condition="openConditionPicker"
              @configure-condition="openConditionConfig"
            />
          </template>

          <template #node-stickyNote="nodeProps">
            <StickyNote
              v-bind="nodeProps"
              @remove="removeNode"
              @change="markDirty"
            />
          </template>

          <!-- Left panel: add-node menu -->
          <Panel position="top-left" class="dc-panel-menu">
            <div class="dc-menu" :class="{ 'dc-menu-collapsed': !menuOpen }">
              <div class="dc-menu-header" @click="menuOpen = !menuOpen">
                <p class="dc-menu-title">
                  {{ $t('FLOWS.EDITOR.ADD_STEP') }}
                </p>
                <svg
                  width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"
                  :style="{
                    color: 'rgb(var(--slate-10))',
                    transform: menuOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                  }"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </div>

              <div v-if="menuOpen" class="dc-menu-items">
                <button
                  v-for="type in PALETTE"
                  :key="type"
                  type="button"
                  class="dc-menu-btn"
                  @click="addNode(type)"
                >
                  <span
                    class="flex items-center justify-center shrink-0"
                    :style="{ color: PALETTE_COLORS[type] }"
                  >
                    <span
                      :class="NODE_TYPES[type].icon"
                      class="w-[15px] h-[15px]"
                    />
                  </span>
                  <span class="dc-menu-btn-label">
                    {{ $t(NODE_TYPES[type].labelKey) }}
                  </span>
                </button>

                <button
                  type="button"
                  class="dc-menu-btn dc-menu-btn-note"
                  @click="addNode('sticky')"
                >
                  <span
                    class="flex items-center justify-center shrink-0"
                    :style="{ color: 'rgb(var(--amber-10))' }"
                  >
                    <span class="i-lucide-sticky-note w-[15px] h-[15px]" />
                  </span>
                  <span class="dc-menu-btn-label">
                    {{ $t('FLOWS.STICKY.LABEL') }}
                  </span>
                </button>
              </div>
            </div>
          </Panel>

          <!-- ========== Floating toolbar (DataCrazy style) ========== -->
          <Panel position="top-center" class="dc-floating-toolbar-panel">
            <div class="dc-floating-toolbar">
              <div
                class="dc-ftb-buttons"
                :class="{ 'dc-ftb-collapsed': toolbarCollapsed }"
              >
                <!-- A: toggle on/off -->
                <button
                  v-if="flow"
                  type="button"
                  class="dc-ftb-btn"
                  :title="flow.active ? $t('FLOWS.ACTIONS.PAUSE') : $t('FLOWS.ACTIONS.ACTIVATE')"
                  @click="toggleActive"
                >
                  <!-- toggle icon inline SVG -->
                  <svg v-if="flow.active" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: rgb(var(--teal-11))">
                    <rect x="1" y="5" width="22" height="14" rx="7" ry="7" /><circle cx="16" cy="12" r="3" />
                  </svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="1" y="5" width="22" height="14" rx="7" ry="7" /><circle cx="8" cy="12" r="3" />
                  </svg>
                </button>

                <!-- B: save -->
                <button
                  type="button"
                  class="dc-ftb-btn"
                  :class="{ 'dc-ftb-btn-save-active': isDirty }"
                  :title="$t('FLOWS.EDITOR.SAVE')"
                  :disabled="isSaving"
                  @click="save"
                >
                  <svg v-if="!isSaving" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" /><path d="M7 3v4a1 1 0 0 0 1 1h7" />
                  </svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="animate-spin">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                </button>

                <!-- C: edit automation -->
                <button
                  type="button"
                  class="dc-ftb-btn"
                  :title="$t('FLOWS.TOOLBAR.EDIT')"
                  @click="openEditModal"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 20h9" /><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
                  </svg>
                </button>

                <!-- D: duplicate automation -->
                <button
                  type="button"
                  class="dc-ftb-btn"
                  :title="$t('FLOWS.ACTIONS.CLONE')"
                  @click="duplicateFlow"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                  </svg>
                </button>

                <!-- E: export JSON -->
                <button
                  type="button"
                  class="dc-ftb-btn"
                  :title="$t('FLOWS.TOOLBAR.EXPORT')"
                  @click="exportFlow"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" />
                  </svg>
                </button>

                <!-- F: add sticky note -->
                <button
                  type="button"
                  class="dc-ftb-btn"
                  :title="$t('FLOWS.STICKY.LABEL')"
                  @click="addNode('sticky')"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z" /><path d="M15 3v4a2 2 0 0 0 2 2h4" />
                  </svg>
                </button>

                <!-- G: delete flow (red) -->
                <button
                  type="button"
                  class="dc-ftb-btn dc-ftb-btn-danger"
                  :title="$t('FLOWS.ACTIONS.DELETE')"
                  @click="deleteFlow"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  </svg>
                </button>
              </div>

              <!-- H: collapse/expand toggle -->
              <button
                type="button"
                class="dc-ftb-btn dc-ftb-toggle"
                @click="toolbarCollapsed = !toolbarCollapsed"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path v-if="toolbarCollapsed" d="m9 18 6-6-6-6" />
                  <path v-else d="m15 18-6-6 6-6" />
                </svg>
              </button>
            </div>
          </Panel>

          <Controls position="bottom-left" class="dc-controls" />
          <MiniMap pannable zoomable class="dc-minimap" />
        </VueFlow>

        <!-- Drop-to-create popup (DataCrazy style) -->
        <div
          v-if="dropPopup"
          class="dc-drop-popup"
          :style="{
            left: dropPopup.screenX + 'px',
            top: dropPopup.screenY + 'px',
          }"
        >
          <div class="dc-drop-popup-inner">
            <button
              v-for="type in PALETTE"
              :key="type"
              type="button"
              class="dc-drop-item"
              @click="onDropSelect(type)"
            >
              <span
                class="dc-drop-item-icon"
                :style="{ color: PALETTE_COLORS[type] }"
              >
                <span
                  :class="NODE_TYPES[type].icon"
                  class="w-[18px] h-[18px]"
                />
              </span>
              <span class="dc-drop-item-label">
                {{ $t(NODE_TYPES[type].labelKey) }}
              </span>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Condition Picker modal -->
    <ConditionPicker
      v-if="conditionPickerBlockId"
      @select="onConditionSelected"
      @close="conditionPickerBlockId = null"
    />

    <!-- Condition Config panel -->
    <ConditionConfig
      v-if="activeCondition"
      :condition="activeCondition"
      :block-id="conditionConfigTarget.blockId"
      @change="markDirty"
      @close="conditionConfigTarget = null"
    />

    <!-- Edit automation modal -->
    <teleport to="body">
      <div v-if="editModalOpen" class="dc-modal-backdrop" @click.self="editModalOpen = false">
        <div class="dc-modal">
          <div class="dc-modal-header">
            <h3 class="dc-modal-title">{{ $t('FLOWS.TOOLBAR.EDIT') }}</h3>
            <button
              type="button"
              class="dc-modal-close"
              @click="editModalOpen = false"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div class="dc-modal-body">
            <label class="dc-modal-label">{{ $t('FLOWS.TOOLBAR.EDIT_NAME') }}</label>
            <input
              v-model="editName"
              type="text"
              class="dc-modal-input"
            />
            <label class="dc-modal-label">{{ $t('FLOWS.TOOLBAR.EDIT_DESCRIPTION') }}</label>
            <input
              v-model="editDescription"
              type="text"
              class="dc-modal-input"
              :placeholder="$t('FLOWS.TOOLBAR.EDIT_DESCRIPTION_PH')"
            />
          </div>
          <div class="dc-modal-footer">
            <button
              type="button"
              class="dc-modal-confirm"
              @click="saveEditModal"
            >
              {{ $t('FLOWS.TOOLBAR.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
/* ===================== Left panel menu ===================== */
.dc-panel-menu {
  margin-top: 1rem;
  margin-left: 0.625rem;
}

.dc-menu {
  width: 14rem;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.dc-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  user-select: none;
}
.dc-menu-header:hover {
  background: rgba(var(--alpha-1));
}

.dc-menu-title {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--slate-12));
}

.dc-menu-items {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0 0.5rem 0.75rem;
}

.dc-menu-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  border: 1px dashed rgb(var(--slate-5));
  background: transparent;
  text-align: left;
  transition: all 0.15s ease;
}
.dc-menu-btn:hover {
  border-color: rgb(var(--blue-9));
  background: rgba(var(--blue-4), 0.08);
}
.dc-menu-btn-note {
  margin-top: 0.25rem;
  border-style: dotted;
}
.dc-menu-btn-note:hover {
  border-color: rgb(var(--amber-9));
  background: rgba(var(--amber-4), 0.08);
}

.dc-menu-btn-label {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12));
}

/* ===================== Floating toolbar ===================== */
.dc-floating-toolbar-panel {
  margin-top: 1rem;
}

.dc-floating-toolbar {
  display: flex;
  align-items: center;
  background: rgb(var(--solid-1));
  border-radius: 1rem;
  border: 1px solid rgb(var(--slate-4));
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 0.25rem;
  backdrop-filter: blur(12px);
}

.dc-ftb-buttons {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  max-width: 600px;
  opacity: 1;
}
.dc-ftb-collapsed {
  max-width: 0;
  opacity: 0;
  padding: 0;
}

.dc-ftb-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem;
  border-radius: 0.625rem;
  color: rgb(var(--slate-10));
  transition: all 0.2s ease;
  flex-shrink: 0;
}
.dc-ftb-btn:hover {
  background: rgba(var(--alpha-2));
  color: rgb(var(--slate-12));
  transform: scale(1.05);
}
.dc-ftb-btn:active {
  transform: scale(0.95);
}

.dc-ftb-btn-save-active {
  color: rgb(var(--blue-11));
}
.dc-ftb-btn-save-active:hover {
  background: rgba(var(--blue-4), 0.2);
  color: rgb(var(--blue-11));
}

.dc-ftb-btn-danger:hover {
  color: rgb(var(--ruby-11));
  background: rgba(var(--ruby-4), 0.2);
}
.dc-ftb-toggle {
  color: rgb(var(--slate-9));
}
.dc-ftb-toggle:hover {
  color: rgb(var(--slate-12));
}

/* ===================== Drop-to-create popup (DataCrazy style) ===================== */
.dc-drop-popup {
  position: fixed;
  z-index: 100;
  transform: translate(-50%, 8px);
}

.dc-drop-popup-inner {
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 15rem;
  animation: dc-popup-in 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes dc-popup-in {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dc-drop-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  border-radius: 10px;
  border: 1px solid rgb(var(--slate-4));
  background: rgb(var(--solid-1));
  text-align: left;
  cursor: pointer;
  transition: all 0.12s ease;
}
.dc-drop-item:hover {
  background: rgba(var(--blue-4), 0.08);
  border-color: rgb(var(--blue-6));
  transform: translateX(2px);
}

.dc-drop-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dc-drop-item-label {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--slate-12));
}

/* ===================== Edit modal ===================== */
.dc-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: dc-fade-in 0.15s ease;
}
@keyframes dc-fade-in {
  from { opacity: 0; }
}

.dc-modal {
  background: rgb(var(--solid-1));
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.16);
  width: 100%;
  max-width: 440px;
  overflow: hidden;
}

.dc-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
}

.dc-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: rgb(var(--slate-12));
}

.dc-modal-close {
  color: rgb(var(--slate-9));
  padding: 0.25rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.dc-modal-close:hover {
  color: rgb(var(--slate-12));
  background: rgba(var(--alpha-2));
}

.dc-modal-body {
  padding: 0 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dc-modal-label {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-11));
  margin-top: 0.25rem;
}

.dc-modal-input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgb(var(--slate-6));
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-12));
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s ease;
}
.dc-modal-input:focus {
  border-color: rgb(var(--blue-9));
  box-shadow: 0 0 0 2px rgba(var(--blue-4), 0.4);
}

.dc-modal-footer {
  padding: 0 1.5rem 1.25rem;
  display: flex;
  justify-content: flex-end;
}

.dc-modal-confirm {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  background: rgb(var(--blue-9));
  color: white;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.15s ease;
}
.dc-modal-confirm:hover {
  background: rgb(var(--blue-10));
}

/* ===================== Edges ===================== */
:deep(.vue-flow__edge-path) {
  stroke: rgb(var(--slate-7));
  stroke-width: 2;
  stroke-dasharray: 5 5;
  transition: stroke 0.15s ease;
}
:deep(.vue-flow__edge:hover .vue-flow__edge-path) {
  stroke: rgb(var(--blue-9));
  stroke-width: 2;
}
:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: rgb(var(--blue-9));
  stroke-width: 3;
  animation: dashdraw 0.2s linear infinite;
}
@keyframes dashdraw {
  to {
    stroke-dashoffset: -10;
  }
}

:deep(.vue-flow__pane) {
  background-color: rgb(var(--background));
  background-image: radial-gradient(circle, #ccc 1px, transparent 1px);
  background-size: 20px 20px;
}
:deep(.vue-flow__handle) {
  border-radius: 50%;
}

:deep(.dc-controls .vue-flow__controls) {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgb(var(--slate-5));
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
:deep(.dc-controls .vue-flow__controls-button) {
  background: rgb(var(--solid-1));
  border: none;
  border-bottom: 1px solid rgb(var(--slate-4));
}
:deep(.dc-controls .vue-flow__controls-button:hover) {
  background: rgba(var(--alpha-2));
}

:deep(.dc-minimap .vue-flow__minimap) {
  border-radius: 8px;
  border: 1px solid rgb(var(--slate-5));
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  background: rgb(var(--solid-1));
}

.inspector-enter-active {
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.inspector-leave-active {
  transition: all 0.2s ease-in;
}
.inspector-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}
.inspector-leave-to {
  opacity: 0;
  transform: translateX(-50%);
}
</style>
